import type {
  FieldFunctionOptions,
  FieldPolicy,
  Reference,
} from '@apollo/client';

type OperationEdge = { cursor?: string; node: Reference };
type OperationsConnection = {
  edges: OperationEdge[];
  pageInfo?: Record<string, unknown>;
  paginated?: boolean;
};

// Alias needed bc field policies apply to every `Query.operations` write in the app
const FEED_ALIAS = 'operationsFeed';

const isFeedField = (field: FieldFunctionOptions['field']) =>
  field?.alias?.value === FEED_ALIAS;

// Deterministic cache key regardless of arg order in the document.
const canonicalKey = (args: Record<string, unknown>) =>
  JSON.stringify(
    Object.fromEntries(
      Object.entries(args).sort(([leftKey], [rightKey]) =>
        leftKey.localeCompare(rightKey),
      ),
    ),
  );

const getNodeID = (
  edge: OperationEdge,
  readField: FieldFunctionOptions['readField'],
) => {
  const node = readField('node', edge);
  return node ? readField('id', node as Reference) : undefined;
};

// Reconciles the feed's two write shapes (everything is deduped by node id):
// - fetchMore (`after` set): append the page after the cursor.
// - initial fetch / poll tick (no `after`): replace the list — unless pages
//   have been accumulated (`paginated` marker), in which case merge the
//   fresh page into the front and keep the tail, so polls never truncate
//   scrolled pages. UPDATED_AT sort bubbles every change into that polled
//   first page, keeping the whole list fresh.
export const operationsFieldPolicy: FieldPolicy<OperationsConnection> = {
  keyArgs: (args, { field }) => {
    if (!args) {
      return false;
    }
    if (!isFeedField(field)) {
      return canonicalKey(args);
    }
    const { first: _first, after: _after, ...rest } = args;
    return canonicalKey(rest);
  },
  merge(existing, incoming, { args, field, readField }) {
    if (!isFeedField(field) || !existing) {
      return incoming;
    }
    if (!incoming) {
      return existing;
    }

    const incomingEdges = incoming.edges ?? [];
    const existingEdges = existing.edges ?? [];

    if (args?.after) {
      const afterIndex = existingEdges.findIndex(
        (edge) => readField('cursor', edge) === args.after,
      );
      const prefix =
        afterIndex >= 0
          ? existingEdges.slice(0, afterIndex + 1)
          : existingEdges;
      const prefixIDs = new Set(
        prefix.map((edge) => getNodeID(edge, readField)),
      );
      const appendedEdges = incomingEdges.filter(
        (edge) => !prefixIDs.has(getNodeID(edge, readField)),
      );
      return {
        ...incoming,
        edges: [...prefix, ...appendedEdges],
        paginated: true,
      };
    }

    if (!existing.paginated) {
      return incoming;
    }

    const incomingIDs = new Set(
      incomingEdges.map((edge) => getNodeID(edge, readField)),
    );
    const remainingEdges = existingEdges.filter(
      (edge) => !incomingIDs.has(getNodeID(edge, readField)),
    );

    return {
      ...existing,
      edges: [...incomingEdges, ...remainingEdges],
      paginated: true,
    };
  },
};
